import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { X, Send, Loader2, CheckCircle2, FileText, GitBranch, Bot, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PriorityBadge } from "@/components/common";

const FIELD = "w-full bg-white border border-border px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand transition-colors";
const LBL = "text-[11px] font-semibold tracking-[0.1em] uppercase text-ink-muted mb-1.5 block";

const JOB_TYPES = [
  "Pricing / Estimate", "Schedule / CPM", "Design Packet", "Structural Coordination",
  "Interior Selections", "Subcontractor Coordination", "Cabinet Shop / Millwork",
  "Digital Services / Product", "General / Internal",
];

export default function NewDirectiveDialog() {
  const { directiveOpen, setDirectiveOpen, companies, companyId } = useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(initial(companyId, companies));

  if (!directiveOpen) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const close = () => {
    setDirectiveOpen(false);
    setResult(null);
    setError("");
    setForm(initial(companyId, companies));
  };

  const submit = async () => {
    setError("");
    if (!form.company_id) return setError("Select a company.");
    if (!form.goal.trim()) return setError("A directive goal is required.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/directives", form);
      setResult(data.result);
      qc.invalidateQueries();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 backdrop-blur-sm py-8 px-4">
      <div className="w-full max-w-3xl bg-white border-2 border-brand shadow-2xl animate-in-up" data-testid="new-directive-dialog">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-ink text-white">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand" style={{ color: "#7CA0FF" }} strokeWidth={2} />
            <h2 className="font-heading text-lg font-bold tracking-tight">{result ? "Directive Processed" : "Submit Directive"}</h2>
          </div>
          <button onClick={close} data-testid="close-directive-btn" className="w-8 h-8 flex items-center justify-center hover:bg-white/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!result ? (
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LBL}>Company *</label>
                <select className={FIELD} value={form.company_id} onChange={set("company_id")} data-testid="directive-company">
                  <option value="">Select company…</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={LBL}>Job Type</label>
                <select className={FIELD} value={form.job_type} onChange={set("job_type")} data-testid="directive-job-type">
                  {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={LBL}>Client</label>
                <input className={FIELD} value={form.client} onChange={set("client")} placeholder="e.g. Harborview Residences" data-testid="directive-client" />
              </div>
              <div>
                <label className={LBL}>Project / Job</label>
                <input className={FIELD} value={form.project} onChange={set("project")} placeholder="e.g. Unit 4B Renovation" data-testid="directive-project" />
              </div>
              <div className="col-span-2">
                <label className={LBL}>Goal *</label>
                <textarea className={FIELD} rows={2} value={form.goal} onChange={set("goal")} placeholder="Describe the directive in plain language…" data-testid="directive-goal" />
              </div>
              <div className="col-span-2">
                <label className={LBL}>Deliverables Required</label>
                <input className={FIELD} value={form.deliverables} onChange={set("deliverables")} placeholder="e.g. Line-item estimate, proposal, cover letter" data-testid="directive-deliverables" />
              </div>
              <div>
                <label className={LBL}>Priority</label>
                <select className={FIELD} value={form.priority} onChange={set("priority")} data-testid="directive-priority">
                  <option value="medium">Medium</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className={LBL}>Deadline</label>
                <input type="date" className={FIELD} value={form.deadline} onChange={set("deadline")} data-testid="directive-deadline" />
              </div>
              <div>
                <label className={LBL}>Folders / Files to Search</label>
                <input className={FIELD} value={form.folders} onChange={set("folders")} placeholder="/projects/harborview/…" data-testid="directive-folders" />
              </div>
              <div>
                <label className={LBL}>People Involved</label>
                <input className={FIELD} value={form.people} onChange={set("people")} placeholder="e.g. PM, estimator" data-testid="directive-people" />
              </div>
              <div className="col-span-2">
                <label className={LBL}>Notes / Constraints</label>
                <textarea className={FIELD} rows={2} value={form.notes} onChange={set("notes")} placeholder="Anything the agents should know…" data-testid="directive-notes" />
              </div>
              <div className="col-span-2 flex items-center gap-3 bg-surface-muted border border-border px-4 py-3">
                <input
                  type="checkbox"
                  id="send-client"
                  checked={form.send_to_client === "Yes"}
                  onChange={(e) => setForm({ ...form, send_to_client: e.target.checked ? "Yes" : "No" })}
                  className="w-4 h-4 accent-brand"
                  data-testid="directive-send-client"
                />
                <label htmlFor="send-client" className="text-sm text-ink flex-1">
                  <span className="font-semibold">Send to client after approval?</span>
                  <span className="text-ink-muted"> — requires owner sign-off before anything goes external.</span>
                </label>
              </div>
            </div>

            {error && <div className="mt-4 px-3 py-2 bg-st-critical/10 border border-st-critical text-sm text-st-critical" data-testid="directive-error">{error}</div>}

            <div className="flex items-center justify-end gap-2 mt-6">
              <button onClick={close} className="px-4 py-2 text-sm border border-border hover:bg-surface-muted">Cancel</button>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="submit-directive-btn"
                className="flex items-center gap-2 bg-brand text-white px-5 py-2 text-sm font-medium hover:bg-brand-dark disabled:opacity-60 transition-colors"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? "Agents working…" : "Run Directive"}
              </button>
            </div>
          </div>
        ) : (
          <ResultView result={result} onClose={close} navigate={navigate} />
        )}
      </div>
    </div>
  );
}

function ResultView({ result, onClose, navigate }) {
  const steps = [
    { icon: GitBranch, label: "Classified", value: `${result.classification.task_label} · ${result.classification.priority}` },
    { icon: Bot, label: "Routed to agent", value: result.agent },
    { icon: FileText, label: "Files discovered", value: `${result.files_used.length} source file(s)` },
    { icon: CheckCircle2, label: "Tasks created", value: `${result.tasks_created} sub-tasks · validated ${result.validation_passed ? "✓" : "⚠"}` },
  ];
  return (
    <div className="p-6" data-testid="directive-result">
      <div className="flex items-center gap-2 text-st-success mb-4">
        <CheckCircle2 className="w-5 h-5" />
        <span className="font-heading font-semibold">Autonomous pipeline complete — every step logged.</span>
      </div>

      <div className="border border-border divide-y divide-border">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3">
            <div className="w-8 h-8 bg-surface-muted flex items-center justify-center shrink-0">
              <s.icon className="w-4 h-4 text-brand" strokeWidth={2} />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-wider text-ink-faint font-semibold">{s.label}</div>
              <div className="text-sm text-ink font-medium">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 bg-surface-muted border border-border p-4">
        <div className="text-[11px] uppercase tracking-wider text-ink-muted font-semibold mb-2">Deliverable</div>
        <div className="font-heading text-base font-semibold text-ink">{result.deliverable}</div>
        <ul className="mt-3 space-y-1.5">
          {result.recommendations.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-ink-muted">
              <ArrowRight className="w-3.5 h-3.5 mt-1 text-brand shrink-0" /> {r}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-end gap-2 mt-6">
        <button onClick={() => { navigate(`/jobs/${result.job_id}`); onClose(); }} className="px-4 py-2 text-sm border border-border hover:bg-surface-muted">View Job</button>
        {result.approval_id ? (
          <button
            onClick={() => { navigate(`/approvals/${result.approval_id}`); onClose(); }}
            data-testid="goto-approval-btn"
            className="flex items-center gap-2 bg-brand text-white px-5 py-2 text-sm font-medium hover:bg-brand-dark"
          >
            <ShieldCheck className="w-4 h-4" /> Review Approval
          </button>
        ) : (
          <button onClick={onClose} className="bg-brand text-white px-5 py-2 text-sm font-medium hover:bg-brand-dark">Done</button>
        )}
      </div>
    </div>
  );
}

function initial(companyId, companies) {
  const cid = companyId && companyId !== "all" ? companyId : (companies[0]?.id || "");
  return {
    company_id: cid, client: "", project: "", job_type: "Pricing / Estimate",
    priority: "medium", goal: "", deliverables: "", deadline: "", folders: "",
    constraints: "", people: "", approval_required_from: "Owner", send_to_client: "Yes", notes: "",
  };
}
